<template>
  <BaseSection heading="Altgerät" data-testid="oldDevice_expansion">
    <PlusButton
      v-if="!hasOldDevice"
      title="Altgerät dokumentieren"
      data-testId="old-device-toggle"
      @click="() => (hasOldDevice = !hasOldDevice)"
    />

    <div v-if="hasOldDevice">
      <div class="flex flex-row items-end justify-end">
        <em
          v-if="!isDisabled"
          class="mdi mdi-close text-2xl"
          data-testId="remove-old-device"
          @click="
            () => {
              resetOldDevice();
              hasOldDevice = false;
            }
          "
        />
      </div>

      <BaseDropdown
        v-if="isSrtReplaceAction"
        v-model:selection="oldDeviceEvent.reason"
        label="Grund auswählen"
        dropdown-text="Grund auswählen"
        :options-list="deviceServiceReasonList"
      />

      <div class="md:flex md:gap-4">
        <BaseFormInput
          v-model="oldDevice.serialNumber"
          class="md:basis-1/2"
          :disabled="isDisabled"
          label="Seriennummer"
          :optional="false"
          :validation="v$.oldDevice.serialNumber"
          test-id="oldDeviceSerialnumber"
        />
        <BaseFormInput
          v-model="oldDevice.customizedNumber"
          class="md:basis-1/2"
          :disabled="isDisabled"
          :validation="v$.oldDevice.customizedNumber"
          label="Artikelnummer"
          test-id="oldDeviceCustomizedNumber"
        />
      </div>
      <div class="md:flex md:gap-4">
        <BaseFormInput
          v-if="!isSmartRadiatorThermostat && !isLoRaWanGateway"
          v-model="oldDevice.productKey"
          :class="[
            ' md:basis-1/2',
            { 'pr-2': isDmgw || isHca || isSmokeDetector },
          ]"
          :disabled="isDisabled"
          :validation="v$.oldDevice.productKey"
          label="Hersteller Artikelnummer"
          test-id="oldDeviceProductkey"
        />
        <BaseFormInput
          v-if="isLoRaWanGateway"
          v-model="oldDevice.eui"
          class="md:basis-1/2 pr-2"
          :disabled="isDisabled"
          :validation="v$.oldDevice.eui"
          label="EUI"
          test-id="oldDeviceEui"
        />
        <BaseFormInput
          v-if="isMeteringDevice"
          v-model="oldDevice.yearOfConformity"
          class="pr-2 md:basis-1/2"
          label="Eichjahr"
          :disabled="isDisabled"
          :validation="v$.oldDevice.yearOfConformity"
          type="number"
          inputmode="number"
          pattern="[0-9]{4}"
          placeholder="yyyy"
          test-id="oldDeviceYearOfConformity"
        />
      </div>
      <div class="md:flex">
        <DateMount
          v-model:device-event="oldDeviceEvent"
          :disabled="isEditMode"
          :validation="v$.oldDeviceEvent"
          class="md:basis-1/2 md:pr-2"
          test-id="unmounting-date"
          label="Demontagedatum"
        />
      </div>
      <div
        v-if="isSmartRadiatorThermostatDevice && batteryPack"
        class="md:flex md:gap-4"
      >
        <BaseFormInput
          v-model="batteryPack.serialNumber"
          class="md:basis-1/2"
          :disabled="isDisabled"
          label="Seriennummer Batterieeinheit"
          :optional="false"
          :validation="v$.oldDevice.serialNumber"
          test-id="oldDeviceSerialnumber"
        />
        <BaseFormInput
          v-model="batteryPack.customizedNumber"
          class="md:basis-1/2"
          :disabled="isDisabled"
          :validation="v$.oldDevice.customizedNumber"
          label="Artikelnummer Batterieeinheit"
          test-id="oldDeviceCustomizedNumber"
        />
      </div>
      <div v-if="isMeasuringDevice" class="md:flex md:gap-4">
        <div class="md:basis-1/2">
          <BaseFormInput
            v-model.number="oldDeviceEvent.counter"
            :disabled="isEditMode"
            label="Zählerstand bei Ausbau"
            inputmode="numeric"
            pattern="[0-9]*"
            :validation="v$.oldDeviceEvent.counter"
            placeholder="0,000"
            test-id="oldDeviceCounterOnUnmount"
            type="number"
          />
        </div>
        <div class="md:basis-1/2">
          <MeasurementUnit
            v-model:unit="oldDevice.measurementUnit"
            :disabled="isEditMode"
            :device="oldDevice"
            :validation="v$.oldDevice.measurementUnit"
            test-id="oldDeviceMeasurementUnit"
          />
        </div>
      </div>
      <div v-if="isHca" class="md:flex md:gap-4">
        <div class="md:basis-1/2">
          <BaseFormInput
            v-model.number="oldDeviceEvent.controlValue"
            :maxlength="2"
            label="Kontrollwert für Verdunster"
            inputmode="numeric"
            pattern="[0-9]{2}"
            placeholder="0"
            test-id="oldDeviceControlValueOnUnmount"
            type="number"
          />
        </div>
        <div class="md:basis-1/2">
          <div class="p-3">
            <div class="relative">
              <label
                class="absolute left-2.5 top-4 z-10 origin-[0] -translate-y-4 scale-75 text-sm text-gray-500 dark:text-gray-400"
              >
                Maßeinheit
              </label>
              <div
                class="px-2.5 pb-2.5 pt-5 text-sm text-gray-900 dark:text-white"
              >
                mm
              </div>
            </div>
          </div>
        </div>
      </div>
      <BaseTextarea
        v-model:input-text="oldDevice.note"
        placeholder="Notizen zum Altgerät"
        label="Notizen zum Altgerät"
        :max-length="NOTE_MAX_LENGTH"
        test-id="oldDeviceNote"
      >
      </BaseTextarea>
    </div>
  </BaseSection>
</template>

<script setup lang="ts">
import MeasurementUnit from '@/components/Forms/DeviceFormComponents/MeasurementUnit.vue';
import { computed, onMounted, ref, WritableComputedRef } from 'vue';
import { DeviceClasses } from '@/models/devices/Device';
import BaseFormInput from '@/components/UI/Input/BaseFormInput.vue';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import { DeviceEventParams } from '@/models/installationPoint/DeviceEvent';
import { deviceTypes, deviceServiceReasonList } from '@/enums/device';
import { NOTE_MAX_LENGTH } from '@/enums/generic';
import { FormAction, useFormStore } from '@/store/form/formStore';
import PlusButton from '@/components/UI/Button/PlusButton.vue';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import DateMount from '@/components/Forms/DeviceFormComponents/DateMount.vue';
import { useValidation } from '@/composables/useValidation';
import {
  isDirectMeterGatewayDevice,
  isHeatCostAllocatorDevice,
  isLoRaWanGatewayDevice,
  isSmartRadiatorThermostatDevice,
  isSmokeDetectorDevice,
} from '@/components/Forms/Helper/deviceTypeHelper';
import BaseDropdown from '@/components/UI/Dropdown/BaseDropdown.vue';
import { useOrderStore } from '@/store/order/orderStore';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { useTKDService } from '@/composables/useTKDService';

const props = defineProps<{
  oldDevice: DeviceClasses;
  oldDeviceEvent: DeviceEventParams;
  installationPoint: InstallationPoint;
  resetOldDevice: () => void;
}>();

const emit = defineEmits(['update:oldDevice', 'update:oldDeviceEvent']);

const hasOldDevice = ref<boolean>(false);
const showReplacementReason = ref<boolean>(false);

const isReplacement = useFormStore().formAction === FormAction.REPLACE;
const hasReplacement =
  props.oldDevice?.serialNumber?.length > 0 &&
  props.oldDeviceEvent.type === 'UNMOUNT_EVENT' &&
  (props.oldDeviceEvent?.reason?.length ?? 0) > 0;

onMounted(() => {
  hasOldDevice.value = !!oldDevice?.value?.serialNumber;
  showReplacementReason.value = isReplacement || hasReplacement;
});

const isDisabled = computed(() => {
  const action = useFormStore().formAction;
  return action === FormAction.REPLACE || action === FormAction.EDIT;
});

const isEditMode = computed(() => {
  return useFormStore().formAction === FormAction.EDIT;
});

const oldDevice: WritableComputedRef<DeviceClasses> = computed({
  get() {
    return props.oldDevice;
  },
  set(value) {
    emit('update:oldDevice', value);
  },
});

const batteryPack = computed(() => {
  const unmountedBatteryPacks = props.installationPoint?.accessories?.filter(
    (service) => service.type === 'SRT_BATTERY_PACK' && service.unmountedAt
  );

  if (!unmountedBatteryPacks)
    return props.installationPoint?.accessories?.filter(
      (service) => service.type === 'SRT_BATTERY_PACK'
    );

  unmountedBatteryPacks.sort(
    (a, b) => new Date(b.unmountedAt) - new Date(a.unmountedAt)
  );

  return unmountedBatteryPacks[0];
});

const oldDeviceEvent: WritableComputedRef<DeviceEventParams> = computed({
  get() {
    return props.oldDeviceEvent;
  },
  set(value) {
    emit('update:oldDeviceEvent', value);
  },
});

const isLoRaWanGateway = computed(() => {
  return isLoRaWanGatewayDevice(oldDevice.value.deviceType);
});

const isSmartRadiatorThermostat = computed(() => {
  return isSmartRadiatorThermostatDevice(oldDevice.value.deviceType);
});

const isSmokeDetector = computed(() => {
  return isSmokeDetectorDevice(oldDevice.value.deviceType);
});

const isDmgw = computed(() => {
  return isDirectMeterGatewayDevice(oldDevice.value.deviceType);
});

const isHca = computed(() => {
  return isHeatCostAllocatorDevice(oldDevice.value.deviceType);
});

const isMeasuringDevice = computed(() => {
  return oldDevice.value.isMeasuringDevice();
});

const isMeteringDevice = computed(() => {
  return oldDevice.value.isMeteringDevice() && !isHca.value;
});

const isSrtReplaceAction = computed(() => {
  return (
    oldDevice.value.deviceType === deviceTypes.SmartRadiatorThermostat &&
    useFormStore().formAction === FormAction.REPLACE
  );
});

const { v$, isFormValid } = useValidation(
  { oldDeviceEvent, oldDevice },
  {
    isReplacement: isReplacement,
    hasReplacement: hasReplacement,
  }
);

defineExpose({
  isFormValid,
});
</script>
<style scoped></style>
